import axios from 'axios';
import instance from './axiosInstance';

// Chatbot API Service
class ChatbotService {
  constructor() {
    this.baseURL = process.env.REACT_APP_CHATBOT_API_URL || 'https://api.openai.com/v1';
    this.apiKey = process.env.REACT_APP_OPENAI_API_KEY;
    this.model = process.env.REACT_APP_CHATBOT_MODEL || 'gpt-3.5-turbo';
  }

  // Generate AI response using OpenAI API
  async generateResponse(message, context = {}) {
    try {
      if (!this.apiKey) {
        throw new Error('OpenAI API key not configured');
      }

      const response = await axios.post(
        `${this.baseURL}/chat/completions`,
        {
          model: this.model,
          messages: [
            {
              role: 'system',
              content: this.getSystemPrompt(context)
            },
            {
              role: 'user',
              content: message
            }
          ],
          max_tokens: 500,
          temperature: 0.7,
          stream: false
        },
        {
          headers: {
            'Authorization': `Bearer ${this.apiKey}`,
            'Content-Type': 'application/json'
          }
        }
      );

      return response.data.choices[0]?.message?.content || 'I apologize, but I could not generate a response.';
    } catch (error) {
      console.error('OpenAI API Error:', error);
      throw new Error('Failed to generate AI response');
    }
  }

  // Get system prompt based on context
  getSystemPrompt(context = {}) {
    const basePrompt = `You are an AI assistant for the IMPRES Alumnus platform. You help users with:
    - Searching for alumni by name, batch, course, or organization
    - Finding information about events and meetings
    - Providing details about alumni chapters
    - Helping with profile updates and account management
    - Answering general questions about the platform
    
    Be helpful, friendly, and professional. If you don't know something specific about the platform, suggest where the user can find the information or ask for more details.`;

    // Add context-specific information
    if (context.userRole) {
      return `${basePrompt}\n\nUser Role: ${context.userRole}`;
    }

    if (context.currentPage) {
      return `${basePrompt}\n\nUser is currently on: ${context.currentPage}`;
    }

    return basePrompt;
  }

  // Search alumni data
  async searchAlumni(query, filters = {}) {
    try {
      const response = await instance.post('/alumni/search', {
        query,
        filters,
        limit: 20
      });
      return response.data;
    } catch (error) {
      console.error('Alumni search error:', error);
      throw new Error('Failed to search alumni');
    }
  }

  // Get events data
  async getEvents(filters = {}) {
    try {
      const response = await instance.get('/AdminMaster/GetChapterEventsAdmin', {
        params: filters
      });
      return response.data;
    } catch (error) {
      console.error('Events fetch error:', error);
      throw new Error('Failed to fetch events');
    }
  }

  // Get chapters data
  async getChapters(filters = {}) {
    try {
      const response = await instance.get('/AdminMaster/GetChapters', {
        params: filters
      });
      return response.data;
    } catch (error) {
      console.error('Chapters fetch error:', error);
      throw new Error('Failed to fetch chapters');
    }
  }

  // Get user profile data
  async getUserProfile() {
    try {
      const response = await instance.get('/user/profile');
      return response.data;
    } catch (error) {
      console.error('Profile fetch error:', error);
      throw new Error('Failed to fetch user profile');
    }
  }

  // Get children details
  async getChildrenDetails(studentId) {
    try {
      const response = await instance.get(`/AlunmusProfile/ChildrenInfo/${studentId}`);
      return response.data;
    } catch (error) {
      console.error('Children details fetch error:', error);
      throw new Error('Failed to fetch children details');
    }
  }

  // Update user profile
  async updateUserProfile(profileData) {
    try {
      const response = await instance.put('/user/profile', profileData);
      return response.data;
    } catch (error) {
      console.error('Profile update error:', error);
      throw new Error('Failed to update profile');
    }
  }

  // Get FAQ data
  async getFAQ() {
    try {
      const response = await instance.get('/faq');
      return response.data;
    } catch (error) {
      console.error('FAQ fetch error:', error);
      // Return static FAQ if API fails
      return this.getStaticFAQ();
    }
  }

  // Static FAQ data (fallback)
  getStaticFAQ() {
    return [
      {
        id: 1,
        question: "How do I search for alumni?",
        answer: "You can search for alumni by name, batch, course, or organization. Use the search bar or ask the AI assistant to help you find specific alumni.",
        category: "Search"
      },
      {
        id: 2,
        question: "How can I join events?",
        answer: "Events are listed in the Events section. You can register for upcoming events and view past event details. The AI assistant can also help you find specific events.",
        category: "Events"
      },
      {
        id: 3,
        question: "What are chapters?",
        answer: "Chapters are regional groups of alumni. You can join chapters based on your location or interests. Each chapter organizes local events and networking opportunities.",
        category: "Chapters"
      },
      {
        id: 4,
        question: "How do I update my profile?",
        answer: "Go to 'My Details' section to update your personal information, contact details, and professional information. The AI assistant can guide you through specific updates.",
        category: "Profile"
      },
      {
        id: 5,
        question: "How do I contact other alumni?",
        answer: "You can search for alumni and view their profiles. If they have shared their contact information, you can reach out to them directly through the platform.",
        category: "Networking"
      },
      {
        id: 6,
        question: "Is there a mobile app?",
        answer: "The platform is fully responsive and works on mobile devices. You can access all features through your mobile browser.",
        category: "Technical"
      }
    ];
  }

  // Get platform statistics
  async getPlatformStats() {
    try {
      const response = await instance.get('/stats');
      return response.data;
    } catch (error) {
      console.error('Stats fetch error:', error);
      // Return mock stats if API fails
      return {
        totalAlumni: 1250,
        activeChapters: 15,
        upcomingEvents: 8,
        totalEvents: 45
      };
    }
  }

  // Send feedback
  async sendFeedback(feedback) {
    try {
      const response = await instance.post('/feedback', feedback);
      return response.data;
    } catch (error) {
      console.error('Feedback send error:', error);
      throw new Error('Failed to send feedback');
    }
  }

  // Get chatbot configuration
  async getChatbotConfig() {
    try {
      const response = await instance.get('/chatbot/config');
      return response.data;
    } catch (error) {
      console.error('Chatbot config fetch error:', error);
      // Return default config
      return {
        enabled: true,
        model: 'gpt-3.5-turbo',
        maxTokens: 500,
        temperature: 0.7,
        quickActions: [
          { text: "Search Alumni", action: "search_alumni" },
          { text: "Find Events", action: "find_events" },
          { text: "Chapter Info", action: "chapter_info" },
          { text: "Profile Help", action: "profile_help" }
        ]
      };
    }
  }

  // Update chatbot configuration
  async updateChatbotConfig(config) {
    try {
      const response = await instance.put('/chatbot/config', config);
      return response.data;
    } catch (error) {
      console.error('Chatbot config update error:', error);
      throw new Error('Failed to update chatbot configuration');
    }
  }

  // Log conversation for analytics
  async logConversation(conversation) {
    try {
      await instance.post('/chatbot/logs', {
        ...conversation,
        timestamp: new Date().toISOString()
      });
    } catch (error) {
      console.error('Conversation log error:', error);
      // Don't throw error for logging failures
    }
  }

  // Get conversation history
  async getConversationHistory(limit = 50) {
    try {
      const response = await instance.get('/chatbot/history', {
        params: { limit }
      });
      return response.data;
    } catch (error) {
      console.error('Conversation history fetch error:', error);
      return [];
    }
  }

  // Clear conversation history
  async clearConversationHistory() {
    try {
      await instance.delete('/chatbot/history');
    } catch (error) {
      console.error('Clear conversation history error:', error);
      throw new Error('Failed to clear conversation history');
    }
  }
}

// Create singleton instance
const chatbotService = new ChatbotService();

export default chatbotService;
